import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { Lock, LockOpen, Clock, CheckCircle2, Loader2, CalendarClock } from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  useTodaysSession,
  useSessionLockStatus,
  useLockSession,
  useUnlockSession,
} from '@/hooks/useDentalSession';
import { useConvertPlanToTreatment } from '@/hooks/useTreatmentPlans';
import { fdiToHaderup } from '@/utils/toothNotation';
import { useAuth } from '@/lib/auth';
import toast from 'react-hot-toast';

// ── Props ─────────────────────────────────────────────────────────────────────

interface SessionPanelProps {
  patientId: string;
  clinicId: string;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function SessionPanel({ patientId, clinicId, className }: SessionPanelProps) {
  const { t } = useTranslation();
  const [noteDraft, setNoteDraft] = useState('');

  const today = new Date().toISOString().slice(0, 10);

  // Realtime subscription hoisted to DentalModulePage (single channel per mount).

  const { data, isLoading } = useTodaysSession(patientId, clinicId);
  const { data: lockRow, isLoading: lockLoading } = useSessionLockStatus(patientId, today);
  const lockSession = useLockSession();
  const unlockSession = useUnlockSession();
  const { user, isClinicAdmin } = useAuth();
  const canUnlock = isClinicAdmin(clinicId);
  const convertPlan = useConvertPlanToTreatment();

  const isLocked = Boolean(lockRow);

  const handleLock = async () => {
    try {
      await lockSession.mutateAsync({ patientId, sessionDate: today, notes: noteDraft || undefined });
      toast.success('Sessionen er låst');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast.error(msg);
    }
  };

  const handleUnlock = async () => {
    if (!lockRow) return;
    try {
      await unlockSession.mutateAsync({ lockId: lockRow.id, patientId, sessionDate: today });
      toast.success('Sessionen er låst op');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast.error(msg);
    }
  };

  const handleMarkCompleted = async (planId: string) => {
    try {
      await convertPlan.mutateAsync({ patientId, planId, clinicId });
      toast.success(t('dental.plan.actions.markCompleted'));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast.error(msg);
    }
  };

  if (isLoading || lockLoading) {
    return (
      <div className={cn('flex items-center justify-center py-8', className)}>
        <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
      </div>
    );
  }

  const { plannedItems = [], completedToday = [], appointment } = data ?? {};

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex-shrink-0">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {t('dental.session.title')}
        </h3>
        <div className="flex items-center gap-2">
          {isLocked && lockRow && (
            <span className="flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400">
              <Lock className="h-3 w-3" />
              Sessionen er låst af {lockRow.locked_by === user?.id ? 'dig' : 'en kollega'}{' '}
              {format(new Date(lockRow.locked_at), 'HH:mm', { locale: da })}
            </span>
          )}
          {isLocked && canUnlock && lockRow && (
            <button
              type="button"
              onClick={handleUnlock}
              disabled={unlockSession.isPending}
              aria-label="Lås session op"
              className="flex items-center gap-1.5 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-100 transition-colors disabled:opacity-50"
            >
              <LockOpen className="h-3.5 w-3.5" />
              Lås op
            </button>
          )}
          {!isLocked && (
            <button
              type="button"
              onClick={handleLock}
              disabled={lockSession.isPending}
              aria-label="Lås session"
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-amber-50 hover:text-amber-700 dark:hover:bg-amber-900/20 transition-colors disabled:opacity-50"
            >
              <Lock className="h-3.5 w-3.5" />
              {t('dental.session.lock')}
            </button>
          )}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 bg-slate-50 dark:bg-slate-950/50">
        {/* Section 1: Today's appointment */}
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">
            {t('dental.session.todaysAppointment')}
          </p>
          {appointment ? (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
              <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <CalendarClock className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                <span className="font-medium">
                  {format(new Date(appointment.starts_at), 'HH:mm', { locale: da })}
                  {appointment.ends_at &&
                    ` – ${format(new Date(appointment.ends_at), 'HH:mm', { locale: da })}`}
                </span>
                {appointment.service_name && (
                  <span className="text-slate-500 dark:text-slate-400">
                    · {appointment.service_name}
                  </span>
                )}
              </div>
              {appointment.clinician_name && (
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                  {appointment.clinician_name}
                </p>
              )}
              {appointment.notes && (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 italic">
                  {appointment.notes}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400 dark:text-slate-500 italic">
              Ingen aftale i dag
            </p>
          )}
        </div>

        {/* Section 2: Today's planned items */}
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">
            {t('dental.session.todaysPlan')}
            {plannedItems.length > 0 && (
              <span className="ml-1 text-slate-300 dark:text-slate-600">
                ({plannedItems.length})
              </span>
            )}
          </p>
          {plannedItems.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500 italic">
              Ingen planlagte elementer for i dag
            </p>
          ) : (
            <div className="space-y-1.5">
              {plannedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-mono font-semibold text-indigo-600 dark:text-indigo-400 mr-1.5">
                      {item.tooth_fdi}
                    </span>
                    <span className="text-xs font-mono text-slate-400 dark:text-slate-500 mr-1.5">
                      {item.treatment_type}
                    </span>
                    <span className="text-sm text-slate-700 dark:text-slate-200 truncate">
                      {item.treatment_description}
                    </span>
                  </div>
                  {!isLocked && (
                    <button
                      type="button"
                      onClick={() => handleMarkCompleted(item.id)}
                      disabled={convertPlan.isPending}
                      className="flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 text-xs font-medium text-white transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {t('dental.plan.actions.markCompleted')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 3: Completed today */}
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">
            {t('dental.session.doneToday')}
            {completedToday.length > 0 && (
              <span className="ml-1 text-slate-300 dark:text-slate-600">
                ({completedToday.length})
              </span>
            )}
          </p>
          {completedToday.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500 italic">
              Ingen behandlinger registreret i dag
            </p>
          ) : (
            <div className="space-y-1.5">
              {completedToday.map((treatment) => (
                <div
                  key={treatment.id}
                  className="flex items-center gap-3 rounded-lg border border-emerald-100 dark:border-emerald-900/30 bg-emerald-50 dark:bg-emerald-950/20 px-3 py-2"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-mono font-semibold text-slate-600 dark:text-slate-300 mr-1.5">
                      {treatment.tooth_fdi}
                    </span>
                    <span className="text-xs font-mono text-slate-400 dark:text-slate-500 mr-1.5">
                      ({fdiToHaderup(treatment.tooth_fdi)})
                    </span>
                    <span className="text-sm text-slate-700 dark:text-slate-200 truncate">
                      {treatment.treatment_description}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 flex-shrink-0">
                    <Clock className="h-3 w-3" />
                    {format(new Date(treatment.created_at), 'HH:mm', { locale: da })}
                  </div>
                  {treatment.actual_cost != null && (
                    <span className="text-xs font-medium text-slate-600 dark:text-slate-300 flex-shrink-0">
                      {treatment.actual_cost.toLocaleString('da-DK')} kr
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Session notes */}
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">
            Note
          </p>
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            disabled={isLocked}
            placeholder={t('dental.session.noteDraftPlaceholder')}
            rows={3}
            className={cn(
              'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900',
              'px-3 py-2 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400',
              'focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none',
              isLocked && 'opacity-60 cursor-not-allowed',
            )}
          />
        </div>
      </div>
    </div>
  );
}
